class Announcement extends HTMLElement {
    private lastScrollTop: number;
    private headerEle: Element | null | undefined;
    private linkRotateInterval: ReturnType<typeof setInterval> | null = null;
    private resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    private abortController = new AbortController();
    private readonly ROTATE_INTERVAL = 3000;
    private readonly FADE_DURATION = 400;
    private readonly MEDIUM_BREAKPOINT = 1200;
    private readonly RESIZE_DEBOUNCE = 150;

    constructor() {
        super();
        this.lastScrollTop = 0;
        this.headerEle = document.querySelector(".section-header");
        this.bindEvents();
    }

    disconnectedCallback() {
        this.abortController.abort();
        if (this.linkRotateInterval) { clearInterval(this.linkRotateInterval); this.linkRotateInterval = null; }
        if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
    }

    bindEvents() {
        const opts = { signal: this.abortController.signal };
        this.announcementToggle();
        window.addEventListener("scroll", this.announcementToggle.bind(this), { passive: true, ...opts });
        this.initLinkRotation();
        window.addEventListener("resize", this.handleResize.bind(this), opts);
    }

    handleResize() {
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }
        this.resizeDebounceTimer = setTimeout(() => {
            this.initLinkRotation();
        }, this.RESIZE_DEBOUNCE);
    }

    initLinkRotation() {
        const links = this.querySelectorAll<HTMLElement>(".header-announcement__link");
        if (links.length < 2) return;

        if (this.linkRotateInterval) {
            clearInterval(this.linkRotateInterval);
            this.linkRotateInterval = null;
        }

        const isMedium = window.innerWidth >= 991 && window.innerWidth < this.MEDIUM_BREAKPOINT;

        const rightContainer = this.querySelector<HTMLElement>(".header-announcement__right");
        if (!rightContainer) return;

        if (isMedium) {
            // Temporarily show all links to measure widths
            links.forEach((link) => {
                link.removeAttribute("data-hidden");
                link.removeAttribute("data-fading");
            });

            // Find the widest link and lock the container width
            let maxWidth = 0;
            links.forEach((link) => {
                maxWidth = Math.max(maxWidth, link.offsetWidth);
            });
            rightContainer.style.width = `${maxWidth}px`;
            rightContainer.setAttribute("data-rotating", "");

            // Now hide all but the first
            links.forEach((link, i) => {
                if (i !== 0) {
                    link.setAttribute("data-hidden", "");
                }
            });

            let activeIndex = 0;
            this.linkRotateInterval = setInterval(() => {
                const current = links[activeIndex];
                const nextIndex = (activeIndex + 1) % links.length;
                const next = links[nextIndex];

                current.setAttribute("data-fading", "");
                setTimeout(() => {
                    current.setAttribute("data-hidden", "");
                    current.removeAttribute("data-fading");
                    next.removeAttribute("data-hidden");
                    next.removeAttribute("data-fading");
                }, this.FADE_DURATION);

                activeIndex = nextIndex;
            }, this.ROTATE_INTERVAL);
        } else {
            rightContainer.style.width = "";
            rightContainer.removeAttribute("data-rotating");
            links.forEach((link) => {
                link.removeAttribute("data-hidden");
                link.removeAttribute("data-fading");
            });
        }
    }

    announcementToggle(event?: Event) {
        const countrySelector = this.querySelector(".country-selector");
        let scrollTop = window.scrollY;
        const megaPanelOpen = this.headerEle?.classList.contains("desktop-mega-panel-open");

        if (scrollTop <= 0) {
            if (!megaPanelOpen) {
                this.classList.remove("hide");
                this.headerEle?.classList.add("with-announcement-bar");
            }
        }
        else if (scrollTop > this.lastScrollTop) {
            this.classList.add("hide");
            this.headerEle?.classList.remove("with-announcement-bar");
            countrySelector?.setAttribute("hidden", "true");
        }
        else {
            if (!megaPanelOpen) {
                this.classList.remove("hide");
                this.headerEle?.classList.add("with-announcement-bar");
            }
        }

        if (scrollTop <= 0) {
            this.headerEle?.classList.remove("sticky-header");
            this.headerEle?.querySelector(".header")?.classList.remove("shadow-elevation-4");
        }
        else {
            this.headerEle?.classList.add("sticky-header");
            this.headerEle?.querySelector(".header")?.classList.add("shadow-elevation-4");
        }

        this.lastScrollTop = scrollTop;
    }
}

customElements.define("header-announcement", Announcement);